import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Coins, Globe, Bitcoin, BarChart3, LineChart } from 'lucide-react';
import apiClient from '@services/api';
import { ticker } from '@utils/helpers';

interface Result {
  symbol: string;
  name: string;
  instrument_type: string;
}

// Turkish gold types are computed from GC=F, so they have pages but no
// instrument row for the API to return
const GOLD_PAGES: Result[] = [
  { symbol: 'gram_altin', name: 'Gram Altın', instrument_type: 'GOLD' },
  { symbol: 'ceyrek_altin', name: 'Çeyrek Altın', instrument_type: 'GOLD' },
  { symbol: 'yarim_altin', name: 'Yarım Altın', instrument_type: 'GOLD' },
  { symbol: 'tam_altin', name: 'Tam Altın', instrument_type: 'GOLD' },
  { symbol: 'ata_altin', name: 'Ata Altın', instrument_type: 'GOLD' },
  { symbol: '22_ayar_bilezik', name: '22 Ayar Bilezik', instrument_type: 'GOLD' },
];

const foldTr = (s: string) =>
  s.replace(/[çÇ]/g, 'c').replace(/[ğĞ]/g, 'g').replace(/[ıIİi]/g, 'i')
   .replace(/[öÖ]/g, 'o').replace(/[şŞ]/g, 's').replace(/[üÜ]/g, 'u')
   .toLowerCase();

const SYMBOL_IS_MEANINGLESS = new Set(['GOLD', 'CURRENCY', 'INDEX']);

const TYPE_LABELS: Record<string, string> = {
  STOCK: 'Hisse', CRYPTO: 'Kripto', CURRENCY: 'Döviz',
  GOLD: 'Altın', INDEX: 'Endeks',
};

const typeIcon = (type: string) => {
  switch (type) {
    case 'GOLD': return <Coins size={15} className="text-gold-400" />;
    case 'CURRENCY': return <Globe size={15} className="text-up-400" />;
    case 'CRYPTO': return <Bitcoin size={15} className="text-accent-alt-400" />;
    case 'INDEX': return <BarChart3 size={15} className="text-accent-400" />;
    default: return <LineChart size={15} className="text-gray-400" />;
  }
};

const InstrumentSearch: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const resp = await apiClient.get<any>(
          `/financial/instruments/?search=${encodeURIComponent(term)}&limit=8`
        );
        const data: any = resp.data;
        const api: Result[] = Array.isArray(data) ? data : data?.results ?? [];
        const gold = GOLD_PAGES.filter((g) => foldTr(g.name).includes(foldTr(term)));
        setResults([...gold, ...api].slice(0, 8));
        setActive(0);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const go = (item: Result) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onNavigate?.();
    navigate(`/instrument/${encodeURIComponent(item.symbol)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Hisse, döviz, altın ara"
        aria-label="Enstrüman ara"
        className="input-dark w-full pl-9 pr-8 py-1.5 text-[13px]"
      />
      {query && (
        <button
          onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
          aria-label="Aramayı temizle"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
        >
          <X size={14} />
        </button>
      )}

      {open && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-1.5 rounded-xl bg-surface border border-white/[0.08] shadow-xl shadow-black/40 overflow-hidden z-50">
          {loading && results.length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-gray-500">Aranıyor…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-gray-500">Sonuç bulunamadı</p>
          ) : (
            results.map((item, i) => (
              <button
                key={item.symbol}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(item)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  i === active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'
                }`}
              >
                {typeIcon(item.instrument_type)}
                {SYMBOL_IS_MEANINGLESS.has(item.instrument_type) ? (
                  // gram_altin and GC=F are internal ids; the name is the label
                  <span className="text-[13px] font-medium text-white truncate">{item.name}</span>
                ) : (
                  <>
                    <span className="text-[13px] font-medium text-white shrink-0">
                      {ticker(item.symbol)}
                    </span>
                    <span className="text-[12px] text-gray-500 truncate">{item.name}</span>
                  </>
                )}
                <span className="ml-auto text-[11px] text-gray-600 shrink-0">
                  {TYPE_LABELS[item.instrument_type] ?? item.instrument_type}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default InstrumentSearch;
